import { withTransientRetry } from "@/pages/home/hooks/use-presentation-generation-flow";
import { presentationGenerationApi } from "@/shared/services/presentation-generation/presentation-generation.api";
import axios from "axios";
import { useEffect } from "react";
import {
  type IState,
  type ImportOption,
  type ImportStore,
  type OptionRef,
} from "../stores/file-to-storydoc-generation.store";
import { fetchOptionSet, regenerationOptions } from "../utils/fetch-option-set";
import { trackImport } from "../utils/track-import";

export const REGEN_POLL_MS = 3000;

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

const rowIndexOf = (store: ImportStore, pageNumber: number) =>
  store.getState().rows.findIndex((r) => r.pageNumber === pageNumber);

const isLive = (
  store: ImportStore,
  jobId: string,
  pageNumber: number,
  regenerationId: string,
) => {
  const s = store.getState();
  return (
    s.jobId === jobId &&
    s.rows.some(
      (r) =>
        r.pageNumber === pageNumber &&
        r.regenerating?.regenerationId === regenerationId,
    )
  );
};

const isAbort = (e: unknown) =>
  axios.isCancel(e) || (e as Error)?.name === "AbortError";

const pollRegeneration = async (
  store: ImportStore,
  jobId: string,
  pageNumber: number,
  regenerationId: string,
  signal: AbortSignal,
) => {
  for (;;) {
    try {
      await sleep(REGEN_POLL_MS, signal);
    } catch {
      return;
    }
    if (!isLive(store, jobId, pageNumber, regenerationId)) return;
    let st: Awaited<
      ReturnType<typeof presentationGenerationApi.getRegeneration>
    >;
    try {
      st = await withTransientRetry(
        () =>
          presentationGenerationApi.getRegeneration(
            jobId,
            pageNumber,
            regenerationId,
            signal,
          ),
        signal,
      );
    } catch (e) {
      if (isAbort(e) || !isLive(store, jobId, pageNumber, regenerationId))
        return;
      store
        .getState()
        .failRegeneration(rowIndexOf(store, pageNumber), regenerationId);
      trackImport("regenerate result", { pageNumber, ok: false }, store);
      return;
    }
    if (!isLive(store, jobId, pageNumber, regenerationId)) return;
    if (st.status === "running") continue;
    const options =
      st.status === "done" ? regenerationOptions(st.options) : null;
    if (options) {
      store
        .getState()
        .applyRegeneration(
          rowIndexOf(store, pageNumber),
          regenerationId,
          options,
        );
      trackImport("regenerate result", { pageNumber, ok: true }, store);
      return;
    }
    store
      .getState()
      .failRegeneration(rowIndexOf(store, pageNumber), regenerationId);
    trackImport(
      "regenerate result",
      {
        pageNumber,
        ok: false,
        code: st.status === "failed" ? st.error?.code : "EMPTY",
      },
      store,
    );
    return;
  }
};

const isPending = (s: IState) =>
  s.jobId != null &&
  (s.status === "generating" || s.status === "done") &&
  s.createdStory == null;

const restoreRegeneratedSet = async (
  store: ImportStore,
  jobId: string,
  pageNumber: number,
  regenerationId: string,
  signal: AbortSignal,
) => {
  const stillWanted = () => {
    const s = store.getState();
    return !signal.aborted && isPending(s) && s.jobId === jobId;
  };
  const fetchSet = (setRef: OptionRef) =>
    withTransientRetry(
      () =>
        fetchOptionSet({
          jobId,
          pageNumber,
          rowIndex: rowIndexOf(store, pageNumber),
          setRef,
          signal,
        }),
      signal,
    );
  let options: ImportOption[];
  try {
    options = await fetchSet({ kind: "regeneration", regenerationId });
  } catch (e) {
    if (isAbort(e) || !stillWanted()) return;
    const original = await fetchSet({ kind: "original" }).catch(() => null);
    if (!stillWanted()) return;
    store
      .getState()
      .restoreOriginalSet(rowIndexOf(store, pageNumber), original);
    return;
  }
  if (!stillWanted()) return;
  store
    .getState()
    .applyRegenerationSet(
      rowIndexOf(store, pageNumber),
      regenerationId,
      options,
    );
};

// Regenerations outlive the modal and reloads: one poller per running
// regeneration id, and one re-fetch per regenerated set missing after a resume.
export const useRegenerationSync = (store: ImportStore) => {
  useEffect(() => {
    const active = new Map<string, AbortController>();
    const restoring = new AbortController();
    const restored = new Set<string>();
    const sync = (s: IState) => {
      const { jobId } = s;
      const pending = isPending(s) && jobId != null;
      const wanted = new Map<string, number>();
      if (pending)
        s.rows.forEach((r) => {
          if (r.regenerating)
            wanted.set(r.regenerating.regenerationId, r.pageNumber);
        });
      active.forEach((ctrl, id) => {
        if (wanted.has(id)) return;
        ctrl.abort();
        active.delete(id);
      });
      wanted.forEach((pageNumber, id) => {
        if (active.has(id) || !jobId) return;
        const ctrl = new AbortController();
        active.set(id, ctrl);
        void pollRegeneration(
          store,
          jobId,
          pageNumber,
          id,
          ctrl.signal,
        ).finally(() => {
          if (active.get(id) === ctrl) active.delete(id);
        });
      });
      if (!pending || !jobId) return;
      s.rows.forEach((r) => {
        if (r.setRef.kind !== "regeneration" || r.options || r.regenerating)
          return;
        const id = r.setRef.regenerationId;
        if (restored.has(id)) return;
        restored.add(id);
        void restoreRegeneratedSet(
          store,
          jobId,
          r.pageNumber,
          id,
          restoring.signal,
        );
      });
    };
    sync(store.getState());
    const unsubscribe = store.subscribe(sync);
    return () => {
      unsubscribe();
      restoring.abort();
      active.forEach((ctrl) => ctrl.abort());
      active.clear();
    };
  }, [store]);
};
